import React from 'react';
import { Typography, Paper, useTheme, useMediaQuery } from '@mui/material';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { Categoria } from '../../../types/consolidatedReport.types';

interface CategoryRadarChartProps {
  categorias: Categoria[];
}

// Parte un texto en líneas de máximo `maxChars` caracteres, respetando palabras
const wrapText = (text: string, maxChars: number): string[] => {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if ((current + (current ? ' ' : '') + word).length <= maxChars) {
      current += (current ? ' ' : '') + word;
    } else {
      if (current) lines.push(current);
      current = word.length > maxChars ? word.substring(0, maxChars) + '…' : word;
    }
  }
  if (current) lines.push(current);
  return lines.slice(0, 3); // máximo 3 líneas
};

// Tick personalizado para PolarAngleAxis con wrapping multilínea
const CustomAngleTick = ({ x, y, payload, isMobile }: any) => {
  const fontSize = isMobile ? 9 : 11;
  const maxChars = isMobile ? 10 : 14;
  const lineHeight = fontSize + 2;
  const lines = wrapText(payload.value, maxChars);

  // Centrar verticalmente el bloque de líneas
  const offsetY = -(lines.length - 1) * lineHeight / 2;

  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontSize, fill: '#555', fontFamily: 'inherit' }}
    >
      {lines.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? offsetY : lineHeight}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

const CategoryRadarChart: React.FC<CategoryRadarChartProps> = ({ categorias }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Datos sin truncamiento: el tick personalizado maneja el wrapping
  const data = categorias.map(cat => ({
    categoria: cat.name,
    valor: Math.round(cat.average)
  }));

  // Más altura cuando hay muchas categorías para que los labels tengan espacio
  const chartHeight = isMobile
    ? Math.max(280, categorias.length * 28)
    : Math.max(380, categorias.length * 32);

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: isMobile ? 2 : 3,
        width: '100%',
        overflow: 'hidden'
      }}
    >
      <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
        Comparación por Categoría
      </Typography>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <RadarChart data={data} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
          <PolarGrid stroke="#e0e0e0" />
          <PolarAngleAxis
            dataKey="categoria"
            tick={(props) => <CustomAngleTick {...props} isMobile={isMobile} />}
          />
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: isMobile ? 8 : 10 }} />
          <Radar name="Cumplimiento" dataKey="valor" stroke="#667eea" fill="#667eea" fillOpacity={0.6} />
          <Tooltip formatter={(value: number) => [`${value}%`, 'Cumplimiento']} />
        </RadarChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default CategoryRadarChart;
